/**
 * Curator Prompts
 *
 * Content curation prompts for evaluating and organizing passages.
 *
 * @module config/prompts/curator-prompts
 */
import { CURATOR_ASSESSMENT_SCHEMA } from '../prompt-output-schema.js';
// ═══════════════════════════════════════════════════════════════════════════
// CURATOR PROMPTS
// ═══════════════════════════════════════════════════════════════════════════
export const CURATOR_PASSAGE_ASSESSMENT = {
    id: 'CURATOR_PASSAGE_ASSESSMENT',
    name: 'Curator Passage Quality Assessment',
    description: 'Evaluate passage on clarity, depth, originality, relevance',
    template: `You are a literary curator assessing passage quality.

Evaluate this passage on four dimensions (0-1 each):
- Clarity: How clear and understandable?
- Depth: How insightful or profound?
- Originality: How unique or fresh?
- Relevance: How relevant to theme "{{theme}}"?

Passage:
{{passage}}

Respond with JSON:
{
  "clarity": 0.0-1.0,
  "depth": 0.0-1.0,
  "originality": 0.0-1.0,
  "relevance": 0.0-1.0,
  "overallQuality": 0.0-1.0,
  "isGem": true/false,
  "reasoning": "Brief explanation"
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 512,
    },
    outputSchema: CURATOR_ASSESSMENT_SCHEMA,
    version: 1,
    usedBy: ['curator'],
};
export const CURATOR_THREAD_COHERENCE = {
    id: 'CURATOR_THREAD_COHERENCE',
    name: 'Curator Thread Coherence Analysis',
    description: 'Assess passage flow, gaps, redundancy, ordering',
    template: `You are analyzing thread coherence for a book chapter.

Evaluate these passages for:
- Flow: Do they connect logically?
- Gaps: Are there missing links?
- Redundancy: Is content repeated?
- Ordering: Is the sequence optimal?

Passages:
{{passages}}

Respond with JSON:
{
  "flowScore": 0.0-1.0,
  "gaps": ["Description of gap"],
  "redundancies": ["Description of redundancy"],
  "suggestedOrder": ["passageId1", "passageId2", ...]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        minContextWindow: 8192,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['curator'],
};
export const CURATOR_CLUSTER_SUGGESTION = {
    id: 'CURATOR_CLUSTER_SUGGESTION',
    name: 'Curator Cluster Identification',
    description: 'Identify thematic clusters in passages',
    template: `Identify {{numClusters}} thematic clusters in these passages.

For each cluster:
- Name: Brief descriptive label
- Theme: Core concept
- Passages: Which passage IDs belong

Passages:
{{passages}}

Respond with JSON:
{
  "clusters": [
    {
      "name": "Cluster name",
      "theme": "Core theme description",
      "passageIds": ["id1", "id2"]
    }
  ]
}`,
    requirements: {
        capabilities: ['json-mode'],
        minContextWindow: 8192,
        temperature: 0.4,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['curator'],
};
export const CURATOR_CARD_ASSIGNMENT = {
    id: 'CURATOR_CARD_ASSIGNMENT',
    name: 'Curator Card-to-Chapter Assignment',
    description: 'Match content cards to chapter sections',
    template: `You are a literary curator helping organize content into chapters.

Match each card to the most appropriate chapter section.

Cards:
{{cards}}

Chapter structure:
{{chapterStructure}}

Respond with JSON:
{
  "assignments": [
    {
      "cardId": "card id",
      "sectionId": "section id",
      "confidence": 0.0-1.0
    }
  ]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['curator'],
};
// ═══════════════════════════════════════════════════════════════════════════
// INLINE MIGRATION PROMPTS (formerly hardcoded in curator)
// ═══════════════════════════════════════════════════════════════════════════
export const CURATOR_INLINE_PASSAGE_ASSESSMENT = {
    id: 'CURATOR_INLINE_PASSAGE_ASSESSMENT',
    name: 'Curator Inline Passage Assessment',
    description: 'Passage quality assessment (migrated from curator.ts inline)',
    template: `You are a literary curator assessing passage quality.
Evaluate on these dimensions (0-1 scale):
- Clarity: How clear and well-written
- Depth: Intellectual or emotional depth
- Originality: Uniqueness of perspective
- Relevance: How well it fits the context

Thread context: {{threadContext}}
Project context: {{projectContext}}

Respond with JSON: { clarity, depth, originality, relevance, concerns: [], recommendations: [] }`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 512,
    },
    version: 1,
    usedBy: ['curator'],
};
export const CURATOR_INLINE_THREAD_COHERENCE = {
    id: 'CURATOR_INLINE_THREAD_COHERENCE',
    name: 'Curator Inline Thread Coherence',
    description: 'Thread coherence analysis (migrated from curator.ts inline)',
    template: `You are analyzing thread coherence for a book chapter.
Theme: {{theme}}

Evaluate:
1. How well do these passages flow together?
2. What topics or perspectives are missing?
3. Are any passages redundant?
4. What order would work best?

Respond with JSON: {
  coherenceScore: 0-1,
  gapAnalysis: [],
  redundancies: [],
  suggestedOrder: [passage indices]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['curator'],
};
export const CURATOR_INLINE_CLUSTER_SUGGESTION = {
    id: 'CURATOR_INLINE_CLUSTER_SUGGESTION',
    name: 'Curator Inline Cluster Suggestion',
    description: 'Thematic cluster identification (migrated from curator.ts inline)',
    template: `Identify {{numClusters}} thematic clusters in these passages.
For each cluster, provide:
- A theme name (2-4 words)
- Which passage indices belong to it
- Suggested thread names for each cluster

Respond with JSON: { clusters: [{ theme, passageIndices: [], suggestedThreads: [] }] }`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.4,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['curator'],
};
export const CURATOR_PROMPTS = [
    CURATOR_PASSAGE_ASSESSMENT,
    CURATOR_THREAD_COHERENCE,
    CURATOR_CLUSTER_SUGGESTION,
    CURATOR_CARD_ASSIGNMENT,
    // Inline migrations
    CURATOR_INLINE_PASSAGE_ASSESSMENT,
    CURATOR_INLINE_THREAD_COHERENCE,
    CURATOR_INLINE_CLUSTER_SUGGESTION,
];
//# sourceMappingURL=curator-prompts.js.map